import { useEffect, useRef, useState } from "react";
import { Send, Loader2, ImagePlus, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, PROJECT_SERVICES } from "@/lib/api";
import { trackEvent } from "@/lib/analytics";

const INITIAL = {
  name: "",
  email: "",
  phone: "",
  service: "webdesign",
  message: "",
  zeitraum: "",
  budget: "",
  address: "",
};

/**
 * Das Anfrage-Formular — wird in der Kontakt-Section UND im Anfrage-Drawer
 * (Overlay, öffnet sich direkt beim Nutzer statt zum Seitenende zu springen)
 * verwendet. idPrefix/testPrefix halten IDs und Test-IDs pro Instanz eindeutig.
 */
export const ContactForm = ({ idPrefix = "cf", testPrefix = "contact", className = "", presetService, onSuccess }) => {
  const [form, setForm] = useState(INITIAL);
  const [photos, setPhotos] = useState([]);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (presetService) setForm((f) => ({ ...f, service: presetService }));
  }, [presetService]);

  useEffect(() => {
    const onPick = (e) => setForm((f) => ({ ...f, service: e.detail }));
    window.addEventListener("jh:service", onPick);
    return () => window.removeEventListener("jh:service", onPick);
  }, []);

  const set = (key) => (e) => {
    setSent(false);
    setForm((f) => ({ ...f, [key]: e.target.value }));
  };

  const addPhotos = (e) => {
    const files = Array.from(e.target.files || []);
    setPhotos((p) =>
      [...p, ...files.map((file) => ({ file, preview: URL.createObjectURL(file) }))].slice(0, 5)
    );
    e.target.value = "";
  };

  const removePhoto = (index) => setPhotos((p) => p.filter((_, i) => i !== index));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      let photoPaths = [];
      if (photos.length > 0) {
        const fd = new FormData();
        photos.forEach((p) => fd.append("files", p.file));
        const { data } = await api.post("/uploads", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        photoPaths = data.paths;
      }
      await api.post("/inquiries", { ...form, photos: photoPaths });
      trackEvent("auftrag_abgeschickt");
      toast.success("Danke für deine Anfrage! Du erhältst gleich eine Bestätigung per E-Mail.");
      setForm(INITIAL);
      setPhotos([]);
      setSent(true);
      onSuccess?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <form onSubmit={submit} data-testid={`${testPrefix}-form`} className={className}>
    <div className="mb-8">
      <label htmlFor={`${idPrefix}-service`} className="block text-xs uppercase tracking-[0.25em] text-zinc-500 mb-1">
        Gewünschte Dienstleistung *
      </label>
      <select
        id={`${idPrefix}-service`}
        data-testid={`${testPrefix}-service-select`}
        value={form.service}
        onChange={set("service")}
        className="field-input appearance-none cursor-pointer"
      >
        {Object.entries(PROJECT_SERVICES).map(([value, label]) => (
          <option key={value} value={value}>
            {label}
          </option>
        ))}
      </select>
    </div>
    <div className="grid sm:grid-cols-2 gap-x-8 gap-y-8 mb-8">
      <div>
        <label htmlFor={`${idPrefix}-name`} className="block text-xs uppercase tracking-[0.25em] text-zinc-500 mb-1">
          Name *
        </label>
        <input
          id={`${idPrefix}-name`}
          data-testid={`${testPrefix}-name-input`}
          required
          value={form.name}
          onChange={set("name")}
          placeholder="Dein Name"
          className="field-input"
        />
      </div>
      <div>
        <label htmlFor={`${idPrefix}-phone`} className="block text-xs uppercase tracking-[0.25em] text-zinc-500 mb-1">
          Telefonnummer *
        </label>
        <input
          id={`${idPrefix}-phone`}
          data-testid={`${testPrefix}-phone-input`}
          required
          value={form.phone}
          onChange={set("phone")}
          placeholder="+49 ..."
          className="field-input"
        />
      </div>
      <div className="sm:col-span-2">
        <label htmlFor={`${idPrefix}-email`} className="block text-xs uppercase tracking-[0.25em] text-zinc-500 mb-1">
          E-Mail *
        </label>
        <input
          id={`${idPrefix}-email`}
          type="email"
          data-testid={`${testPrefix}-email-input`}
          required
          value={form.email}
          onChange={set("email")}
          placeholder="du@beispiel.de"
          className="field-input"
        />
      </div>
      <div>
        <label htmlFor={`${idPrefix}-zeitraum`} className="block text-xs uppercase tracking-[0.25em] text-zinc-500 mb-1">
          Gewünschter Zeitraum
        </label>
        <input
          id={`${idPrefix}-zeitraum`}
          data-testid={`${testPrefix}-zeitraum-input`}
          value={form.zeitraum}
          onChange={set("zeitraum")}
          placeholder="z. B. nächstes Wochenende"
          className="field-input"
        />
      </div>
      <div>
        <label htmlFor={`${idPrefix}-budget`} className="block text-xs uppercase tracking-[0.25em] text-zinc-500 mb-1">
          Budget / weitere Infos (optional)
        </label>
        <input
          id={`${idPrefix}-budget`}
          data-testid={`${testPrefix}-budget-input`}
          value={form.budget}
          onChange={set("budget")}
          placeholder="z. B. max. 5.000 €"
          className="field-input"
        />
      </div>
      <div className="sm:col-span-2">
        <label htmlFor={`${idPrefix}-address`} className="block text-xs uppercase tracking-[0.25em] text-zinc-500 mb-1">
          Adresse / Ort (optional)
        </label>
        <input
          id={`${idPrefix}-address`}
          data-testid={`${testPrefix}-address-input`}
          value={form.address}
          onChange={set("address")}
          placeholder="Wo soll der Auftrag erledigt werden?"
          className="field-input"
        />
      </div>
    </div>
    <div className="mb-10">
      <label htmlFor={`${idPrefix}-message`} className="block text-xs uppercase tracking-[0.25em] text-zinc-500 mb-1">
        Kurze Beschreibung *
      </label>
      <textarea
        id={`${idPrefix}-message`}
        data-testid={`${testPrefix}-message-input`}
        required
        rows={4}
        value={form.message}
        onChange={set("message")}
        placeholder="Beschreib kurz, worum es geht ..."
        className="field-input resize-none"
      />
    </div>
    <div className="mb-10">
      <p className="text-xs uppercase tracking-[0.25em] text-zinc-500 mb-1">
        Fotos hochladen (optional)
      </p>
      <p className="text-sm text-zinc-500 mb-4">
        Zeige uns gerne vorab, was gemacht werden soll. So können wir den
        Aufwand besser einschätzen. (max. 5 Fotos, JPG/PNG/WEBP)
      </p>
      <input
        ref={fileRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        multiple
        onChange={addPhotos}
        className="hidden"
        data-testid={`${testPrefix}-photos-input`}
      />
      <div className="flex flex-wrap gap-3" data-testid={`${testPrefix}-photos-preview`}>
        {photos.map((p, i) => (
          <div key={p.preview} className="relative w-20 h-20 border border-black/10 overflow-hidden">
            <img src={p.preview} alt={`Foto ${i + 1}`} className="w-full h-full object-cover" />
            <button
              type="button"
              onClick={() => removePhoto(i)}
              data-testid={`${testPrefix}-photo-remove-${i}`}
              aria-label="Foto entfernen"
              className="absolute top-1 right-1 bg-ink/70 text-paper p-0.5"
            >
              <X size={12} />
            </button>
          </div>
        ))}
        {photos.length < 5 && (
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            data-testid={`${testPrefix}-add-photos-button`}
            className="w-20 h-20 border border-dashed border-black/25 flex flex-col items-center justify-center gap-1 text-zinc-400 hover:border-ink hover:text-ink transition-colors duration-300"
          >
            <ImagePlus size={18} />
            <span className="text-[9px] uppercase tracking-widest">Foto</span>
          </button>
        )}
      </div>
    </div>
    <button
      type="submit"
      disabled={sending}
      data-testid={`${testPrefix}-submit-button`}
      className="group relative overflow-hidden w-full bg-ink text-paper py-4 text-xs uppercase tracking-[0.3em] disabled:opacity-50 disabled:cursor-not-allowed"
    >
      <span className="absolute inset-0 bg-gold translate-y-full group-hover:translate-y-0 transition-transform duration-500 ease-[cubic-bezier(0.16,1,0.3,1)]" />
      <span className="relative flex items-center justify-center gap-3 group-hover:text-ink transition-colors duration-500">
        {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
        {sending ? "Wird gesendet ..." : "Anfrage senden"}
      </span>
    </button>
    {sent && (
      <div
        data-testid={`${testPrefix}-success-message`}
        className="border border-gold/40 bg-gold/5 p-5 text-sm text-ink flex items-start gap-3"
      >
        <Send size={16} className="text-gold mt-0.5 shrink-0" />
        <p>
          <b>Danke für deine Anfrage!</b> Sie ist bei mir eingegangen — du erhältst gleich eine
          Bestätigung per E-Mail. Ich melde mich schnellstmöglich bei dir.
        </p>
      </div>
    )}
    </form>
  );
};
